import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import { CookieConsentBanner } from '../components/cookie-consent/cookie-consent-banner';
import { CookiePreferencesDialog } from '../components/cookie-consent/cookie-preferences-dialog';
import {
  CONSENT_CHANGED_EVENT,
  ESSENTIAL_ONLY,
  readConsent,
  writeConsent,
  ACCEPT_ALL,
} from '../lib/consent';
import type { ConsentPreferences, StoredConsent } from '../lib/consent';

interface CookieConsentContextValue {
  /** `null` until the visitor has made a choice — that's also the banner-visible condition. */
  consent: StoredConsent | null;
  /**
   * False until the post-hydration effect has read storage. The banner must
   * stay unrendered until then — see the provider's note on hydration.
   */
  hasReadStoredConsent: boolean;
  isPreferencesOpen: boolean;
  openPreferences: () => void;
  closePreferences: () => void;
  acceptAll: () => void;
  rejectAll: () => void;
  savePreferences: (preferences: ConsentPreferences) => void;
}

const CookieConsentContext = createContext<CookieConsentContextValue | null>(null);

/**
 * Owns cookie-consent state for the whole app and renders the banner +
 * preferences dialog once at the root, so any page (e.g. the footer's
 * "Cookie preferences" link) can reopen the dialog without prop drilling.
 *
 * Opt-in by construction: `consent` starts `null` (nothing beyond essential
 * is treated as allowed) until `acceptAll`/`rejectAll`/`savePreferences` is
 * called — there's no non-essential script anywhere in this app yet to gate,
 * but this is the seam a future analytics/marketing integration would check.
 *
 * Storage is deliberately NOT read while rendering: this app is pre-rendered
 * (SSG), and the pre-render cannot know a visitor's stored decision. Reading
 * it in a `useState` initializer made the first client render disagree with
 * the server HTML for any returning visitor, and React does not remove the
 * extra server-rendered node from the root container on that mismatch — the
 * banner was left in the DOM with no fiber attached: never translated, never
 * dismissable, buttons inert. So the first client render must match the
 * server (no banner), and the decision is read in the effect below.
 */
export function CookieConsentProvider({ children }: { children: ReactNode }) {
  const [consent, setConsent] = useState<StoredConsent | null>(null);
  const [hasReadStoredConsent, setHasReadStoredConsent] = useState(false);
  const [isPreferencesOpen, setPreferencesOpen] = useState(false);

  useEffect(() => {
    const handleExternalChange = () => setConsent(readConsent());
    handleExternalChange();
    setHasReadStoredConsent(true);
    window.addEventListener(CONSENT_CHANGED_EVENT, handleExternalChange);
    window.addEventListener('storage', handleExternalChange);
    return () => {
      window.removeEventListener(CONSENT_CHANGED_EVENT, handleExternalChange);
      window.removeEventListener('storage', handleExternalChange);
    };
  }, []);

  const persist = useCallback((preferences: ConsentPreferences) => {
    setConsent(writeConsent(preferences));
    setPreferencesOpen(false);
  }, []);

  const value = useMemo<CookieConsentContextValue>(
    () => ({
      consent,
      hasReadStoredConsent,
      isPreferencesOpen,
      openPreferences: () => setPreferencesOpen(true),
      closePreferences: () => setPreferencesOpen(false),
      acceptAll: () => persist(ACCEPT_ALL),
      rejectAll: () => persist(ESSENTIAL_ONLY),
      savePreferences: persist,
    }),
    [consent, hasReadStoredConsent, isPreferencesOpen, persist],
  );

  return (
    <CookieConsentContext.Provider value={value}>
      {children}
      <CookieConsentBanner />
      <CookiePreferencesDialog />
    </CookieConsentContext.Provider>
  );
}

export function useCookieConsent(): CookieConsentContextValue {
  const context = useContext(CookieConsentContext);
  if (!context) {
    throw new Error('useCookieConsent must be used within a CookieConsentProvider');
  }
  return context;
}
